import { useEffect, useMemo, useState } from 'react';
import type { OrgMembership } from '@/backend/contracts';
import { useAppStore } from '../../../store/useAppStore';
import { getOrganization } from '../api/org-api';
import {
    getActiveOrgIdPreference,
    getUserOrgMemberships,
} from '../api/org-membership-api';
import type { Organization } from '../org-types';
import { logger } from '@/utils/logger';

export type OrganizationContextStatus =
    | 'loading'
    | 'ready'
    | 'needs-setup'
    | 'needs-selection'
    | 'error';

export interface OrganizationContext {
    status: OrganizationContextStatus;
    organization: Organization | null;
    activeOrgId: string | null;
    memberships: OrgMembership[];
    error: string | null;
}

const INITIAL_CONTEXT: OrganizationContext = {
    status: 'loading',
    organization: null,
    activeOrgId: null,
    memberships: [],
    error: null,
};

interface LoadedOrganizationContext extends OrganizationContext {
    userId: string;
}

function isAdminOrgMembership(membership: OrgMembership): boolean {
    return membership.role === 'owner' || membership.role === 'admin';
}

function chooseActiveAdminOrgId(
    memberships: OrgMembership[],
    preferredOrgId: string | null
): string | null {
    const adminMemberships = memberships.filter(isAdminOrgMembership);

    if (preferredOrgId && adminMemberships.some((membership) => membership.orgId === preferredOrgId)) {
        return preferredOrgId;
    }

    if (preferredOrgId) {
        return null;
    }

    if (adminMemberships.length === 1) {
        return adminMemberships[0]?.orgId || null;
    }

    return null;
}

function getNoActiveAdminOrgStatus(memberships: OrgMembership[]): OrganizationContextStatus {
    return memberships.length === 0 ? 'needs-setup' : 'needs-selection';
}

function toOrganizationContext(state: LoadedOrganizationContext): OrganizationContext {
    return {
        status: state.status,
        organization: state.organization,
        activeOrgId: state.activeOrgId,
        memberships: state.memberships,
        error: state.error,
    };
}

export function useOrganizationContext(): OrganizationContext {
    const userId = useAppStore((state) => state.auth.user?.uid);
    const cloudEnabled = useAppStore((state) => state.cloud.enabled);
    const [loadedState, setLoadedState] = useState<LoadedOrganizationContext | null>(null);
    const useVisualLocalDemoContext = import.meta.env.VITE_VISUAL_LOCAL_DEMO === 'true' && !cloudEnabled;

    useEffect(() => {
        if (!userId || useVisualLocalDemoContext) {
            return;
        }

        const currentUserId = userId;
        let cancelled = false;

        async function loadOrganizationContext() {
            try {
                const [memberships, preferredOrgId] = await Promise.all([
                    getUserOrgMemberships(currentUserId),
                    getActiveOrgIdPreference(currentUserId),
                ]);

                if (cancelled) return;

                const activeOrgId = chooseActiveAdminOrgId(memberships, preferredOrgId);
                if (!activeOrgId) {
                    setLoadedState({
                        userId: currentUserId,
                        status: getNoActiveAdminOrgStatus(memberships),
                        organization: null,
                        activeOrgId: null,
                        memberships,
                        error: null,
                    });
                    return;
                }

                const organization = await getOrganization(activeOrgId);
                if (cancelled) return;

                setLoadedState({
                    userId: currentUserId,
                    status: organization ? 'ready' : 'error',
                    organization,
                    activeOrgId,
                    memberships,
                    error: organization ? null : 'Active organization was not found',
                });
            } catch (error: unknown) {
                logger.error('Failed to load organization context:', error);
                if (!cancelled) {
                    setLoadedState({
                        userId: currentUserId,
                        status: 'error',
                        organization: null,
                        activeOrgId: null,
                        memberships: [],
                        error: 'Failed to load organization context',
                    });
                }
            }
        }

        void loadOrganizationContext();

        return () => {
            cancelled = true;
        };
    }, [userId, useVisualLocalDemoContext]);

    return useMemo(() => {
        if (useVisualLocalDemoContext) {
            return {
                status: 'needs-setup',
                organization: null,
                activeOrgId: null,
                memberships: [],
                error: null,
            };
        }

        if (!userId) {
            return {
                status: 'needs-setup',
                organization: null,
                activeOrgId: null,
                memberships: [],
                error: null,
            };
        }

        if (!loadedState || loadedState.userId !== userId) {
            return INITIAL_CONTEXT;
        }

        return toOrganizationContext(loadedState);
    }, [loadedState, userId, useVisualLocalDemoContext]);
}
